import { useContext, useEffect, useMemo } from "react";
import { Collapse, List, ListItemButton, ListItemIcon, ListItemText } from "@mui/material";
import PinDropIcon from "@mui/icons-material/PinDrop";
import ExpandLess from "@mui/icons-material/ExpandLess";
import ExpandMore from "@mui/icons-material/ExpandMore";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import { useNavigate, useParams, useMatch } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { CORPORATE_BACKOFFICE_PATHS } from "constants/constants";
import { CorporateContext } from "contexts/CorporateContext/CorporateContext";
import useMenuItemExpand from "components/Layout/AppDrawer/hooks/useMenuItemExpand";
import { useStyles } from "components/Layout/AppDrawer/components/DrawerItem.styles";
import { HMIContext } from "contexts/HMIContext/HMIContext";
import { ShiftOptionsEnum } from "components/ShiftDropdown/ShiftDropdown.view";

const LocationsMenu = () => {
    const classes = useStyles();
    const { t } = useTranslation();
    const { isMenuItemExpanded, toggleMenuItemExpanded, addMenuItemsExpanded } = useMenuItemExpand();
    const navigate = useNavigate();
    const routeParams = useParams();
    const millId = useMemo(() => Number(routeParams.millId), [routeParams.millId]);
    const matchPath = useMatch(
        `/${CORPORATE_BACKOFFICE_PATHS.ROOT_PATH}/${CORPORATE_BACKOFFICE_PATHS.LOCATIONS.ROOT}/${CORPORATE_BACKOFFICE_PATHS.LOCATIONS.VARIABLE}`,
    );
    const {
        corporations,
        divisions,
        millsWithNoDivision,
        isLoadingCorporationsAndDivisions,
        refreshCorporationsAndDivisions,
    } = useContext(CorporateContext);
    const { handleShift } = useContext(HMIContext);

    useEffect(() => {
        if (!isLoadingCorporationsAndDivisions && corporations) {
            const isInMillsWithNoDivision = millsWithNoDivision.some((mill) => mill.mill_id === millId);
            if (isInMillsWithNoDivision) {
                addMenuItemsExpanded(["locations"]);
                return;
            }

            corporations.forEach((corporation) => {
                const divisions = corporation.divisions || [];
                divisions.forEach((division) => {
                    const { division_id, regions } = division;
                    regions.forEach((region) => {
                        const { region_id, mills } = region;
                        mills?.forEach((mill) => {
                            const { mill_id } = mill;
                            if (mill_id === millId) {
                                addMenuItemsExpanded(["locations", `division${division_id}`, `region${region_id}`]);
                            }
                        });
                    });
                });
            });
        } else {
            if (matchPath) {
                addMenuItemsExpanded(["locations"]);
            }
        }
    }, [isLoadingCorporationsAndDivisions, corporations, millsWithNoDivision]); // eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => {
        refreshCorporationsAndDivisions();
    }, [refreshCorporationsAndDivisions]);

    if (isLoadingCorporationsAndDivisions && !corporations) {
        return (
            <div className={classes.loadingMenu}>
                {t("corporate_backoffice.corporate_layout.app_drawer.locations_menu.loading")}
            </div>
        );
    }

    return (
        <>
            <ListItemButton onClick={() => toggleMenuItemExpanded("locations")} className={classes.drawerItem}>
                <ListItemIcon>
                    <PinDropIcon className={classes.drawerItemIcon} />
                </ListItemIcon>
                <ListItemText
                    primary={t("corporate_backoffice.corporate_layout.app_drawer.locations_menu.title")}
                    className={classes.drawerItemText}
                />
                {isMenuItemExpanded("locations") ? (
                    <ExpandLess className={classes.chevronIcon} />
                ) : (
                    <ExpandMore className={classes.chevronIcon} />
                )}
            </ListItemButton>

            <Collapse in={isMenuItemExpanded("locations")} timeout="auto" unmountOnExit>
                <List component="div" disablePadding>
                    {millsWithNoDivision.map((mill) => {
                        const { mill_id, name } = mill;
                        return (
                            <ListItemButton
                                sx={{ pl: 8 }}
                                onClick={() => {
                                    navigate(
                                        `/${CORPORATE_BACKOFFICE_PATHS.ROOT_PATH}/${CORPORATE_BACKOFFICE_PATHS.LOCATIONS.ROOT}/${mill_id}`,
                                    );
                                    handleShift(ShiftOptionsEnum.current);
                                }}
                                className={`${classes.drawerItem} ${
                                    millId === mill_id ? classes.drawerItemSelected : ""
                                }`}
                                key={mill_id}
                            >
                                <ListItemText primary={name} className={classes.drawerItemText} />
                            </ListItemButton>
                        );
                    })}
                </List>
            </Collapse>
            {divisions.map((division) => {
                const { division_id, regions } = division;
                const divisionName = division.name;
                return (
                    <Collapse in={isMenuItemExpanded("locations")} timeout="auto" unmountOnExit key={division_id}>
                        <List component="div" disablePadding>
                            <ListItemButton
                                sx={{ pl: 8 }}
                                onClick={() => toggleMenuItemExpanded(`division${division_id}`)}
                                className={classes.drawerItem}
                            >
                                {isMenuItemExpanded(`division${division_id}`) ? (
                                    <ExpandMore className={classes.chevronIcon} />
                                ) : (
                                    <ChevronRightIcon className={classes.chevronIcon} />
                                )}
                                <ListItemText primary={divisionName} className={classes.drawerItemText} />
                            </ListItemButton>
                            <Collapse in={isMenuItemExpanded(`division${division_id}`)} timeout="auto" unmountOnExit>
                                <List component="div" disablePadding>
                                    {regions.map((region) => {
                                        const { region_id, name, mills } = region;

                                        return (
                                            <Collapse
                                                in={isMenuItemExpanded(`division${division_id}`)}
                                                timeout="auto"
                                                unmountOnExit
                                                key={region_id}
                                            >
                                                <List component="div" disablePadding>
                                                    <ListItemButton
                                                        sx={{ pl: 16 }}
                                                        onClick={() => toggleMenuItemExpanded(`region${region_id}`)}
                                                        className={classes.drawerItem}
                                                    >
                                                        {isMenuItemExpanded(`region${region_id}`) ? (
                                                            <ExpandMore className={classes.chevronIcon} />
                                                        ) : (
                                                            <ChevronRightIcon className={classes.chevronIcon} />
                                                        )}

                                                        <ListItemText
                                                            primary={name}
                                                            className={classes.drawerItemText}
                                                        />
                                                    </ListItemButton>
                                                    <Collapse
                                                        in={isMenuItemExpanded(`region${region_id}`)}
                                                        timeout="auto"
                                                        unmountOnExit
                                                    >
                                                        <List component="div" disablePadding>
                                                            {mills?.length ? (
                                                                mills.map((mill) => {
                                                                    const { mill_id, name } = mill;
                                                                    return (
                                                                        <ListItemButton
                                                                            sx={{ pl: 24 }}
                                                                            onClick={() => {
                                                                                navigate(
                                                                                    `/${CORPORATE_BACKOFFICE_PATHS.ROOT_PATH}/${CORPORATE_BACKOFFICE_PATHS.LOCATIONS.ROOT}/${mill_id}`,
                                                                                );
                                                                                handleShift(ShiftOptionsEnum.current);
                                                                            }}
                                                                            className={`${classes.drawerItem} ${
                                                                                millId === mill_id
                                                                                    ? classes.drawerItemSelected
                                                                                    : ""
                                                                            }`}
                                                                            key={mill_id}
                                                                        >
                                                                            <ListItemText
                                                                                primary={name}
                                                                                className={classes.drawerItemText}
                                                                            />
                                                                        </ListItemButton>
                                                                    );
                                                                })
                                                            ) : (
                                                                <ListItemButton
                                                                    sx={{ pl: 16 }}
                                                                    className={classes.drawerItem}
                                                                >
                                                                    <ListItemText
                                                                        primary={`(${t(
                                                                            "corporate_backoffice.corporate_layout.app_drawer.locations_menu.no_mills_found",
                                                                        )})`}
                                                                        className={classes.drawerItemTextNoData}
                                                                    />
                                                                </ListItemButton>
                                                            )}
                                                        </List>
                                                    </Collapse>
                                                </List>
                                            </Collapse>
                                        );
                                    })}
                                </List>
                            </Collapse>
                        </List>
                    </Collapse>
                );
            })}
        </>
    );
};

export default LocationsMenu;
